import React, { useState, useEffect, useRef } from 'react';
import {
  Button,
  Form,
  Input,
  Radio,
  Select,
  DatePicker,
  message,
  Tooltip,
  Modal,
  Space,
  Checkbox,
  Spin,
} from 'antd';
import { LeftOutlined, QuestionCircleOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import './step2.scss';
import {
  ClientAgreementsToSignRecord,
  createNaturalPersonAndAccounts,
  signApexAgreements,
  isAmplifyError,
  getRiaProfile,
  addAccounts,
  getUserData,
  createRiskScore,
  signWavvestAgreements,
  ApexAgreementsToSignRecord,
  storePortfolioData,
  getVerificationResult,
  fetchApexOnboardingPrefill,
} from '@/api/custom';
import {
  accountTypeToName,
  GoalType,
  RetirementType,
  Portfolio,
  AccountType,
  GetAccountsResponse,
  implementRecommendation,
  setAllocationForAllAccounts,
  AddAccountRequest,
  accountInfoFromApi,
  formatAccounts,
  ASSET_LIABILITY,
} from '@/util/planning-util';
import {
  ApexUserInformation,
  Address,
  EmploymentStatusType,
  TaxIdType,
  IRSFormType,
  UsdRangeType,
  InvestmentExperience,
  MaritalStatusType,
  LargeTrader,
  TrustedContact,
  CountryType,
  NameSuffixType,
  InputRow,
  Recommendation,
  TimeHorizonCollection,
  validateName,
  validateE164PhoneNumber,
  validateOccupation,
  validateEmployer,
  US_STATES,
  ISO_3166_1_ALPHA_2_CODES,
  getCountryName,
  parseMiddleNames,
  validateMiddleNames,
} from '@/util/account-openning-util';
import {
  updateUserInfo,
  phrase1Complete,
  phrase2Complete,
  phrase3Complete,
  phrase4Complete,
  phrase5Complete,
  setApexAccounts,
  // setAccountDetail,
} from '@/store/features/apex/apexSlice';
import {
  selectApexAccounts,
  selectApexLoading,
  selectApexUserInfo,
} from '@/store/features/apex/apex-selectors';
import { ApexAccount } from '@/types/wealth';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { AsYouType, parsePhoneNumberWithError } from 'libphonenumber-js';
import { selectRiaProfile, selectUser } from '@/store/features/userinfo/userinfoSlice';
import { selectKycResult } from '@/store/features/plaid/identity-selectors';
import { setKycResult } from '@/store/features/plaid/identitySlice';
import dayjs from 'dayjs';
import {
  // fetchAccountDetails,
  fetchApexAccounts,
} from '@/store/features/apex/apex-thunks';
import { components } from '@/util/apex-client-gen';
import {
  hasCompleteApexPersonalInfoForKycSkip,
  getMissingApexPersonalInfoFromKyc,
  getMissingApexPersonalInfoFromFirmData,
  type ApexOnboardingPrefill,
  mapVerificationResultToKycResult,
} from './kyc-prefill';
import { useFirmDataEnabled } from '@/hooks/useFirmDataEnabled';

const { Option } = Select;

const LabelWithTooltip: React.FC<{ label: string; tooltip: string }> = ({ label, tooltip }) => (
  <span>
    {label}
    <Tooltip title={tooltip} placement="top">
      <QuestionCircleOutlined
        style={{
          marginLeft: 8,
          color: '#8c8c8c',
          cursor: 'help',
        }}
      />
    </Tooltip>
  </span>
);

interface PersonalInfoFormData {
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix: NameSuffixType;
  phoneNumber: string;
  dateOfBirth?: Date;
  personalAddress: Address;
  countryOfCitizenship: string;
}

interface EmploymentInfoFormData {
  employmentStatus: EmploymentStatusType;
  occupation?: string; // when employmentStatus is EMPLOYED or SELF_EMPLOYED, occupation is required
  employer?: string;
  startYear?: string;
  employerAddress?: Address;
}

interface TaxInfoFormData {
  taxId: string;
  taxIdType: TaxIdType;
  federalTaxClassification?: string;
  irsFormType?: IRSFormType;
  usITINStatus?: 'US_TIN_STATUS_UNSPECIFIED' | 'PASSING' | 'FAILING';
}

interface IncomeInfoFormData {
  annualIncomeRangeUsd: UsdRangeType;
  totalNetWorthRangeUsd: UsdRangeType;
}

interface OptionalInfoFormData {
  investmentExperience?: InvestmentExperience;
  maritalStatus?: MaritalStatusType;
  isAdvisor?: boolean;
  isInstitutionalCustomer?: boolean;
  isCorrespondentEmployee?: boolean;
  isLargeTrader?: boolean;
  largeTrader?: LargeTrader;
  controlPersonCompanySymbols?: string[];
  finraAssociatedEntity?: string;
  politicallyExposedFamilyNames?: string;
  politicallyExposedOrganization?: string;
  trustedContacts?: TrustedContact[];
}

interface ComplianceInfoFormData {
  complianceQuestions?: string[];
}

interface AllFormData {
  selectedGoals: GoalType[];
  selectedRetirementTypes: RetirementType[];
  accountTypesToCreate: AccountType[];
  personalInfo: PersonalInfoFormData | null;
  employmentInfo: EmploymentInfoFormData | null;
  taxInfo: TaxInfoFormData | null;
  incomeInfo: IncomeInfoFormData | null;
  optionalInfo: OptionalInfoFormData | null;
  complianceInfo: ComplianceInfoFormData | null;
  agreementAccepted: boolean;
  recommendation: Recommendation;
  timeHorizons: TimeHorizonCollection;
}

const LETTER_3210_ALERT =
  'Since you are affiliated with FINRA entity or Apex Fintech Solutions, Letter 3210 is required to fully open your accounts. Please contact Wavvest support to provide the required document.';

// PhoneNumberInput component - defined outside to prevent state reset on parent re-renders
const PhoneNumberInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}> = ({ value, onChange, placeholder }) => {
  const [displayValue, setDisplayValue] = useState('');
  const prevValueRef = useRef<string>('');

  useEffect(() => {
    // Only update displayValue if the value actually changed
    if (value === prevValueRef.current) {
      return;
    }

    prevValueRef.current = value;

    try {
      if (value && value !== '+1') {
        const parsed = parsePhoneNumberWithError(value);
        setDisplayValue(new AsYouType('US').input(parsed.nationalNumber));
      } else {
        setDisplayValue('');
      }
    } catch (_) {
      setDisplayValue('');
    }
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const formattedNumber = new AsYouType().input(inputValue);
    setDisplayValue(formattedNumber);
  };

  // validate and update real value when blur
  const handleBlur = () => {
    try {
      const parsed = parsePhoneNumberWithError('+1' + displayValue.replace(/\D/g, ''), 'US');
      if (parsed.isValid()) {
        onChange(parsed.number); // output E.164 format
      } else {
        onChange('+1'); // default US area code
      }
    } catch (_) {
      onChange('+1');
    }
  };

  return (
    <Space.Compact style={{ display: 'flex' }}>
      <Input style={{ width: '15%', marginRight: '5px' }} value="🇺🇸 +1" disabled />
      <Input
        style={{ width: '85%' }}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        maxLength={14}
      />
    </Space.Compact>
  );
};

const Step2: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [agreementsLoading, setAgreementsLoading] = useState(false);
  const [currentPhase, setCurrentPhase] = useState(1);
  const [isResolvingPersonalInfo, setIsResolvingPersonalInfo] = useState(true);
  const [showTrustedContact, setShowTrustedContact] = useState(false);
  const [isLargeTrader, setIsLargeTrader] = useState(false);

  const [employmentStatus, setEmploymentStatus] = useState<string>('');
  const [showFinraEntity, setShowFinraEntity] = useState(false);
  const [showControlPerson, setShowControlPerson] = useState(false);
  const [showPoliticallyExposedFamily, setShowPoliticallyExposedFamily] = useState(false);
  const [showPoliticallyExposedOrg, setShowPoliticallyExposedOrg] = useState(false);
  const attemptedKycResultPrefillRef = useRef(false);
  const [trustedContactCountry, setTrustedContactCountry] = useState<CountryType>('US');
  const [agreements, setAgreements] = useState<ClientAgreementsToSignRecord[]>([]);
  const [apexAgreements, setApexAgreements] = useState<ApexAgreementsToSignRecord[]>([]);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const [portfolios, setPortfolios] = useState<Partial<Record<GoalType, Portfolio>>>({});

  const userInfo = useAppSelector(selectApexUserInfo);
  const apexAccounts = useAppSelector(selectApexAccounts);
  const riaProfile = useAppSelector(selectRiaProfile);
  const user = useAppSelector(selectUser);
  const apexLoading = useAppSelector(selectApexLoading);
  const kycResult = useAppSelector(selectKycResult);
  const { enabled: firmDataEnabled, loading: firmDataLoading } = useFirmDataEnabled(
    user?.client_id,
  );

  // The profile request can resolve after the investor starts typing. Reading
  // from this ref at the point of applying a response keeps late prefill from
  // replacing values that are now present in the form.
  const latestUserInfoRef = useRef(userInfo);
  latestUserInfoRef.current = userInfo;

  const [allFormData, setAllFormData] = useState<AllFormData>({
    selectedGoals: [],
    selectedRetirementTypes: [],
    accountTypesToCreate: [],
    recommendation: {
      'Capital Preservation': 0,
      Growth: 0,
    },
    timeHorizons: {},
    personalInfo: null,
    employmentInfo: null,
    taxInfo: null,
    incomeInfo: null,
    optionalInfo: null,
    complianceInfo: null,
    agreementAccepted: false,
  });

  const totalPhases = 6;

  const saveRiskScoresForAccounts = async (
    accountIds: string[],
    riskScore: number,
  ): Promise<void> => {
    if (!Number.isFinite(riskScore)) {
      throw new Error('Cannot save risk scores: invalid risk score');
    }
    const user = await getUserData();
    const userId = user?.cognito_id;
    const firmId = user?.client_id;
    if (!userId || !firmId) {
      throw new Error('Cannot save risk scores: missing user or firm identity');
    }

    const uniqueAccountIds = Array.from(
      new Set(accountIds.filter((id) => typeof id === 'string' && id.length)),
    );
    if (uniqueAccountIds.length === 0) {
      throw new Error('Cannot save risk scores: no account ids to persist');
    }

    await Promise.all(
      uniqueAccountIds.map((accountId) =>
        createRiskScore({
          user_id: userId,
          firm_id: firmId,
          account_id: accountId,
          risk_score: riskScore,
        }),
      ),
    );
  };

  // update field in store
  const updateFieldInStore = (fieldName: string, value: any) => {
    dispatch(updateUserInfo({ [fieldName]: value }));
  };

  // update nested field in store
  const updateNestedFieldInStore = (parentField: string, childField: string, value: any) => {
    const currentParentValue = (userInfo as any)[parentField] || {};
    dispatch(
      updateUserInfo({
        [parentField]: {
          ...currentParentValue,
          [childField]: value,
        },
      }),
    );
  };

  // update trusted contacts in store
  const updateTrustedContactInStore = (field: string, value: any) => {
    const currentTrustedContacts = userInfo.trustedContacts || [];
    const currentContact = currentTrustedContacts[0] || {
      familyName: '',
      givenName: '',
      middleNames: [],
      phoneNumber: '',
      email: '',
      mailingAddress: {
        line1: '',
        line2: '',
        line3: '',
        city: '',
        state: '',
        postalCode: '',
        country: 'US' as CountryType,
      },
    };

    // Deep copy to avoid mutating readonly objects from Redux store
    const updatedContact = {
      ...currentContact,
      middleNames: [...(currentContact.middleNames || [])],
      mailingAddress: { ...(currentContact.mailingAddress || {}) },
    };

    if (field === 'trustedContactGivenName') {
      updatedContact.givenName = value || '';
    } else if (field === 'trustedContactFamilyName') {
      updatedContact.familyName = value || '';
    } else if (field === 'trustedContactMiddleNames') {
      updatedContact.middleNames = value ? parseMiddleNames(value) : [];
    } else if (field === 'trustedContactPhone') {
      updatedContact.phoneNumber = value || '';
    } else if (field === 'trustedContactEmail') {
      updatedContact.email = value || '';
    } else if (field === 'trustedContactAddressLine1') {
      updatedContact.mailingAddress.line1 = value || '';
    } else if (field === 'trustedContactAddressLine2') {
      updatedContact.mailingAddress.line2 = value || '';
    } else if (field === 'trustedContactAddressCity') {
      updatedContact.mailingAddress.city = value || '';
    } else if (field === 'trustedContactAddressState') {
      updatedContact.mailingAddress.state = value || '';
    } else if (field === 'trustedContactAddressPostalCode') {
      updatedContact.mailingAddress.postalCode = value || '';
    } else if (field === 'trustedContactAddressCountry') {
      updatedContact.mailingAddress.country = value || 'US';
    }

    dispatch(
      updateUserInfo({
        trustedContacts: [updatedContact],
      }),
    );
  };

  // check if there are existing apexAccounts, if so, skip to phase 6
  useEffect(() => {
    const state = location.state as any;
    // normal flow, set form data but start from phase 1
    setAllFormData((prev) => ({
      ...prev,
      selectedGoals: state.selectedGoals,
      selectedRetirementTypes: state.selectedRetirementTypes,
      accountTypesToCreate: state.accountTypesToCreate,
      timeHorizons: state.timeHorizons,
      recommendation: state.recommendation,
    }));
    if (state?.hasExistingAccounts) {
      const inputRows: InputRow[] = state.selectedGoals
        .map((goal: GoalType) => {
          const timeHorizon = state.timeHorizons[goal];
          if (typeof timeHorizon !== 'number') return null;
          return {
            goal,
            recommendation: state.recommendation,
            timeHorizon,
            retirementTypes: state.selectedRetirementTypes,
          };
        })
        .filter((row: InputRow | null): row is InputRow => row !== null);
      (async () => {
        let accountsForPlan = apexAccounts;
        const apexEnabled =
          Boolean(riaProfile.profile?.apex_correspondent_id) &&
          Boolean(riaProfile.features?.apex_enabled);
        if (apexEnabled && accountsForPlan.length === 0) {
          try {
            const request = dispatch(fetchApexAccounts()) as {
              unwrap?: () => Promise<ApexAccount[]>;
            };
            if (typeof request?.unwrap === 'function') {
              const fetched = await request.unwrap();
              if (Array.isArray(fetched)) {
                accountsForPlan = fetched;
              }
            }
          } catch (error) {
            console.error('Failed to fetch Apex accounts for add-account flow', error);
          }
        }
        try {
          await implementPlan(inputRows, accountsForPlan, state?.recommendation?.Growth ?? 0);
          setCurrentPhase(6);
        } catch (error) {
          console.error('Failed to implement plan for existing accounts', error);
          message.error('Failed to save risk score for new accounts.');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [location.state]);

  // restore form data when phase changes
  useEffect(() => {
    // delay execution to ensure form is rendered
    const timer = setTimeout(() => {
      restoreFormDataFromStore(currentPhase);
    }, 100);

    return () => clearTimeout(timer);
  }, [currentPhase, userInfo]);

  const handleBack = () => {
    if (
      currentPhase === 1 ||
      (currentPhase === 2 && hasCompleteApexPersonalInfoForKycSkip(userInfo))
    ) {
      navigate('/wealth/open-brokerage-account/step1');
    } else {
      setCurrentPhase(currentPhase - 1);
    }
  };

  const handleNext = async () => {
    try {
      setLoading(true);

      // validate current step
      const isValid = validateCurrentStepWithStore(currentPhase);
      if (!isValid) {
        message.error('Please complete all required fields');
        setLoading(false);
        return;
      }

      // if current phase is 5, show submit confirm modal
      if (currentPhase === 5) {
        setLoading(false);
        showSubmitConfirmModal();
        return;
      } else if (currentPhase === 6) {
        // Phase 6: sign agreements
        await signAgreementsHandler();
      } else if (currentPhase < totalPhases) {
        setCurrentPhase(currentPhase + 1);
        // don't reset form, because it will be restored in useEffect
        // form.resetFields();
        // don't reset state, because it will be handled in restoreFormDataFromStore
        // setShowTrustedContact(false);
        // setIsLargeTrader(false);
        // setSelectedCompliance([]);
      }
    } catch (error) {
      console.error('Error saving data:', error);
      message.error('Error saving data, please try again');
    } finally {
      setLoading(false);
    }
  };

  const implementPlan = async (
    inputRows: InputRow[],
    apexAccounts: ApexAccount[],
    riskScore: number,
  ) => {
    setLoading(true);
    const apexEnabled = riaProfile.features?.apex_enabled;
    const state = location.state as any;
    const hasExistingAccounts = state?.hasExistingAccounts;

    // 1. call implementRecommendation for each goal to get new portfolio
    const newPf: Partial<Record<GoalType, Portfolio>> = {};
    inputRows.forEach((row) => {
      newPf[row.goal] = implementRecommendation(
        row.goal,
        row.recommendation,
        row.timeHorizon as number,
        portfolios[row.goal],
        row.retirementTypes,
      );
    });
    setPortfolios(newPf as Record<GoalType, Portfolio>);

    await storePortfolioData(newPf);

    // 2a. setAdvisorArch -
    await setAllocationForAllAccounts(apexAccounts, newPf as Record<GoalType, Portfolio>);

    // 2b. handleAddAccount if there are existing apexAccounts and account types to create
    if (apexEnabled && hasExistingAccounts && state.accountTypesToCreate.length > 0) {
      await createAccountsIfNeeded(state.accountTypesToCreate, riskScore);
    }
  };

  const createAccountsIfNeeded = async (
    accountTypesToCreate: AccountType[],
    riskScore: number,
    // apexAccounts: ApexAccount[],
  ) => {
    try {
      const addAccountsRequest: AddAccountRequest = {
        account_types: accountTypesToCreate,
      };
      const newAccounts = (await addAccounts(addAccountsRequest)).accounts.map(accountInfoFromApi);
      const newBaseAccounts: ApexAccount[] = newAccounts.map((account) => ({
        id: account.account_detail?.account_id ?? '',
        name: account.account_detail?.account_number ?? '',
        type: account.goal,
        asset_or_liability: ASSET_LIABILITY.ASSET,
        total_equity: parseFloat(account.total_equity || '0'),
        holdings: account.holdings,
        allocation: account.advisor_arch_goal,
        transferList: {
          deposits: account.deposits,
          withdrawals: account.withdrawals,
        },
        cash_available_for_withdraw: account.cash_available_for_withdraw,
        ira_contribution_constraints: account.ira_contribution_constraints,
        ira_distribution_constraints: account.ira_distribution_constraints,
        account_detail: account.account_detail,
      }));

      const currentAccounts = [...apexAccounts, ...newBaseAccounts];

      dispatch(setApexAccounts(currentAccounts));
      try {
        await saveRiskScoresForAccounts(
          newBaseAccounts.map((a) => a.id),
          riskScore,
        );
      } catch (error) {
        console.error('Failed to save risk scores for new accounts', error);
        message.warning(
          'Accounts were created, but saving risk score failed. Please contact support if this persists.',
        );
      }
      // const response = await addAccounts(addAccountsRequest);
      // const newBaseAccounts: ApexAccount[] = response.apexAccounts.map(
      //   (account) => ({
      //     id: account.account_detail?.account_id ?? "",
      //     name: account.account_detail?.account_number ?? "",
      //     type: account.goal,
      //     total_equity: parseFloat(account.total_equity || "0"),
      //     holdings: account.holdings,
      //     allocation: account.advisor_arch_goal,
      //     transferList: {
      //       deposits: account.deposits,
      //       withdrawals: account.withdrawals,
      //     },
      //   }),
      // );
      // const currentAccounts = [...apexAccounts, ...newBaseAccounts];

      // dispatch(setApexAccounts(currentAccounts));
    } catch (error) {
      console.error('Failed to add apexAccounts:', error);
      throw error;
    }
  };

  // restore form data from store
  const restoreFormDataFromStore = (phase: number) => {
    let formValues: Record<string, unknown> = {};

    switch (phase) {
      case 1:
        formValues = {
          firstName: userInfo.firstName || '',
          middleName: userInfo.middleName || '',
          lastName: userInfo.lastName || '',
          suffix: userInfo.suffix || 'NAME_SUFFIX_UNSPECIFIED',
          phoneNumber: userInfo.phoneNumber || '',
          dateOfBirth: userInfo.dateOfBirth
            ? dayjs(new Date(userInfo.dateOfBirth).toISOString().split('T')[0])
            : null,
          personalAddress: {
            line1: userInfo.personalAddress?.line1 || '',
            line2: userInfo.personalAddress?.line2 || '',
            line3: userInfo.personalAddress?.line3 || '',
            city: userInfo.personalAddress?.city || '',
            state: userInfo.personalAddress?.state || '',
            postalCode: userInfo.personalAddress?.postalCode || '',
            country: userInfo.personalAddress?.country || 'US',
          },
        };
        break;

      case 2:
        formValues = {
          employmentStatus: userInfo.employmentStatus || '',
          occupation: userInfo.occupation || '',
          employer: userInfo.employer || '',
          startYear: userInfo.startYear || '',
        };
        setEmploymentStatus(userInfo.employmentStatus || '');
        break;

      case 3:
        formValues = {
          taxId: userInfo.taxId || '',
          taxIdType: userInfo.taxIdType || 'TAX_ID_TYPE_SSN',
          federalTaxClassification: userInfo.federalTaxClassification,
          irsFormType: userInfo.irsFormType,
          usITINStatus: userInfo.usITINStatus,
        };
        break;

      case 4:
        formValues = {
          annualIncomeRangeUsd: userInfo.annualIncomeRangeUsd,
          totalNetWorthRangeUsd: userInfo.totalNetWorthRangeUsd,
        };
        break;

      case 5: {
        const trustedContact = userInfo.trustedContacts?.[0];

        // Convert largeTrader effectiveDate from object to dayjs
        let largeTraderFormValue = undefined;
        if (userInfo.largeTrader) {
          largeTraderFormValue = {
            id: userInfo.largeTrader.id,
            effectiveDate: userInfo.largeTrader.effectiveDate
              ? dayjs()
                  .year(userInfo.largeTrader.effectiveDate.year)
                  .month(userInfo.largeTrader.effectiveDate.month - 1)
                  .date(userInfo.largeTrader.effectiveDate.day)
              : undefined,
          };
        }

        formValues = {
          investmentExperience: userInfo.investmentExperience,
          maritalStatus: userInfo.maritalStatus,
          isAdvisor: userInfo.isAdvisor,
          isInstitutionalCustomer: userInfo.isInstitutionalCustomer,
          isCorrespondentEmployee: userInfo.isCorrespondentEmployee,
          isLargeTrader: userInfo.isLargeTrader,
          largeTrader: largeTraderFormValue,
          controlPersonCompanySymbols: userInfo.controlPersonCompanySymbols?.join(', '),
          finraAssociatedEntity: userInfo.finraAssociatedEntity,
          politicallyExposedFamilyNames: userInfo.politicallyExposedFamilyNames,
          politicallyExposedOrganization: userInfo.politicallyExposedOrganization,
          isFinraAffiliated: userInfo.isFinraAffiliated ?? !!userInfo.finraAssociatedEntity,
          isControlPerson:
            userInfo.isControlPerson ?? !!userInfo.controlPersonCompanySymbols?.length,
          hasPoliticallyExposedFamily:
            userInfo.hasPoliticallyExposedFamily ?? !!userInfo.politicallyExposedFamilyNames,
          hasPoliticallyExposedOrg:
            userInfo.hasPoliticallyExposedOrganization ?? !!userInfo.politicallyExposedOrganization,
          // Trusted contact fields
          trustedContactGivenName: trustedContact?.givenName,
          trustedContactFamilyName: trustedContact?.familyName,
          trustedContactMiddleNames: trustedContact?.middleNames?.join(' '),
          trustedContactPhone: trustedContact?.phoneNumber,
          trustedContactEmail: trustedContact?.email,
          trustedContactAddressLine1: trustedContact?.mailingAddress?.line1,
          trustedContactAddressLine2: trustedContact?.mailingAddress?.line2,
          trustedContactAddressCity: trustedContact?.mailingAddress?.city,
          trustedContactAddressState: trustedContact?.mailingAddress?.state,
          trustedContactAddressPostalCode: trustedContact?.mailingAddress?.postalCode,
          trustedContactAddressCountry: trustedContact?.mailingAddress?.country || 'US',
        };

        // restore extra state
        setIsLargeTrader(userInfo.isLargeTrader || false);
        setShowTrustedContact(!!(trustedContact?.givenName || trustedContact?.familyName));
        setTrustedContactCountry((trustedContact?.mailingAddress?.country as CountryType) || 'US');
        setShowFinraEntity(userInfo.isFinraAffiliated ?? !!userInfo.finraAssociatedEntity);
        setShowControlPerson(
          userInfo.isControlPerson ?? !!userInfo.controlPersonCompanySymbols?.length,
        );
        setShowPoliticallyExposedFamily(
          userInfo.hasPoliticallyExposedFamily ?? !!userInfo.politicallyExposedFamilyNames,
        );
        setShowPoliticallyExposedOrg(
          userInfo.hasPoliticallyExposedOrganization ?? !!userInfo.politicallyExposedOrganization,
        );
        break;
      }
    }

    // set form values
    form.setFieldsValue(formValues);
  };

  // validate current step with store
  const validateCurrentStepWithStore = (phase: number): boolean => {
    switch (phase) {
      case 1:
        return phrase1Complete(userInfo);
      case 2:
        return phrase2Complete(userInfo);
      case 3:
        return phrase3Complete(userInfo);
      case 4:
        return phrase4Complete(userInfo);
      case 5:
        return phrase5Complete(userInfo);
      case 6:
        return allFormData.agreementAccepted;
      default:
        return true;
    }
  };

  const handleSkip = async () => {
    // skip agreements, submit data
    await submitHandler();
  };

  // show submit confirmation modal
  const [isSubmitConfirmModalVisible, setIsSubmitConfirmModalVisible] = useState(false);

  // show success modal
  const [isSuccessModalVisible, setIsSuccessModalVisible] = useState(false);

  const showSubmitConfirmModal = () => {
    setIsSubmitConfirmModalVisible(true);
  };

  const handleSubmitConfirm = async () => {
    setIsSubmitConfirmModalVisible(false);
    try {
      setLoading(true);
      const createdAccounts = await submitHandler();
      if (!createdAccounts?.length) {
        return;
      }
      const inputRows = allFormData.selectedGoals
        .map((goal) => {
          const timeHorizon = allFormData.timeHorizons[goal];
          if (typeof timeHorizon !== 'number') return null;
          return {
            goal,
            recommendation: allFormData.recommendation,
            timeHorizon,
            retirementTypes: allFormData.selectedRetirementTypes,
          } as InputRow;
        })
        .filter((row): row is InputRow => row !== null);
      await implementPlan(inputRows, createdAccounts, allFormData.recommendation.Growth);
    } catch (error) {
      console.error('Error submitting data:', error);
      message.error('Error submitting data, please try again');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitCancel = () => {
    setIsSubmitConfirmModalVisible(false);
  };

  const handleBackToHomepage = () => {
    setIsSuccessModalVisible(false);
    navigate('/');
  };

  async function signAgreementsHandler() {
    try {
      setLoading(true);

      if (agreements && agreements.length > 0) {
        const newAccounts = new Set();

        const signRequests = apexAgreements.map(({ account, agreements }) => {
          newAccounts.add(account.id!);
          return {
            account_id: account.id!,
            agreement_ids: agreements.map((a) => a.agreement_id!),
          };
        });
        await signApexAgreements({ entries: signRequests });

        if (agreements.length > 0) {
          const riaAgreementIds = agreements.map((a) => a.agreement_id);
          await signWavvestAgreements(riaAgreementIds);
        }

        // const latestAccounts = await getApexData();
        // dispatch(updateAccountsFromApi(latestAccounts));
        dispatch(fetchApexAccounts());

        // const latestAccounts = await getApexData();
        // const baseAccounts: ApexAccount[] = latestAccounts.apexAccounts.map(
        //   (account) => ({
        //     id: account.account_id,
        //     name: account.account_number,
        //     type: accountTypeToName(account.account_investment_type),
        //     total_equity: parseFloat(account.total_equity || "0"),
        //   }),
        // );
        // dispatch(setApexAccounts(baseAccounts));
        // dispatch(fetchApexAccounts());
      }

      // show success modal after signing agreements
      setIsSuccessModalVisible(true);
    } catch (error) {
      console.error('Error signing agreements:', error);
      message.error('Failed to sign agreements. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  async function submit(
    userInfo: ApexUserInformation,
    enabledAccountType: AccountType[],
  ): Promise<GetAccountsResponse | undefined> {
    try {
      const accounts = await createNaturalPersonAndAccounts(userInfo, enabledAccountType);
      return accounts;
    } catch (e) {
      let message;
      if (isAmplifyError(e)) {
        if (e.response.data && 'detail' in e.response.data) {
          message = e.response.data.detail;
        }
        if (e.response.status === 503) {
          message =
            'The request has timed out. Perhaps the internet is too slow. Please try again later.';
        } else if (e.response.status === 409) {
          message =
            'Some of the accounts already exists. Perhaps the network is slow, and not all accounts have been loaded. Please go to home page and refresh to see which accounts have been opened.';
        } else if (e.response.status === 500) {
          message = 'Remote server error. Please try again later.';
        } else {
          message = 'Remote error: ' + JSON.stringify(e.response);
        }
      } else {
        message = 'Error: ' + JSON.stringify(e);
      }
      console.error('there is an error: ', message, e);
      return undefined;
    }
  }

  async function submitHandler(): Promise<ApexAccount[] | null> {
    const enabledAccountType = allFormData.accountTypesToCreate;
    let createdAccounts: ApexAccount[] | null = null;

    const submitResponse = await submit(userInfo, enabledAccountType as AccountType[]);

    if (!submitResponse) {
      message.error('Failed to create accounts, please try again later.');
      return null;
    } else {
      const accounts = formatAccounts(submitResponse);

      const newBaseAccounts: ApexAccount[] = accounts.map((account) => ({
        id: account.account_detail?.account_id ?? '',
        name: account.account_detail?.account_number ?? '',
        type: account.goal,
        asset_or_liability: ASSET_LIABILITY.ASSET,
        total_equity: parseFloat(account.total_equity || '0'),
        holdings: account.holdings,
        allocation: account.advisor_arch_goal,
        transferList: {
          deposits: account.deposits,
          withdrawals: account.withdrawals,
        },
        cash_available_for_withdraw: account.cash_available_for_withdraw,
        ira_contribution_constraints: account.ira_contribution_constraints,
        ira_distribution_constraints: account.ira_distribution_constraints,
        account_detail: account.account_detail,
      }));

      dispatch(setApexAccounts(newBaseAccounts));
      try {
        await saveRiskScoresForAccounts(
          newBaseAccounts.map((a) => a.id),
          allFormData.recommendation.Growth,
        );
      } catch (error) {
        console.error('Failed to save risk scores for new accounts', error);
        message.warning(
          'Accounts were created, but saving risk score failed. Please contact support if this persists.',
        );
      }
      createdAccounts = newBaseAccounts;
    }

    const requireLetter3210 =
      (userInfo.finraAssociatedEntity && userInfo.finraAssociatedEntity.length > 0) ||
      userInfo.isCorrespondentEmployee;

    if (submitResponse && requireLetter3210) {
      const alertMessage = `${LETTER_3210_ALERT}. Your account IDs are ${submitResponse.accounts.map((a) => a.account_detail.account_number)}`;

      Modal.warning({
        title: 'Notice',
        content: alertMessage,
        okText: 'Home',
        onOk: () => {
          navigate('/');
        },
      });
    }

    if (submitResponse?.accounts.length === enabledAccountType.length) {
      setTimeout(() => {
        setCurrentPhase(currentPhase + 1);
      });
    } else {
      if (!submitResponse || submitResponse.accounts.length === 0) {
        message.error('Failed to create accounts; please contact support.');
      } else {
        const successes = submitResponse.accounts.map((a) => a.goal);
        const failed = enabledAccountType.filter((a) => !successes.includes(a));
        const errorMessage = `Some of your accounts have failed to be opened: \n ${failed.join(', ')}. Please retry later or contact support.`;
        Modal.error({
          title: 'Error',
          content: errorMessage,
          okText: 'Continue',
          onOk: () => {
            setCurrentPhase(currentPhase + 1);
          },
        });
      }
    }
    return createdAccounts;
  }

  const getTitle = () => {
    if (currentPhase <= 5) {
      return 'Open your investment account';
    } else {
      // check if it is from existing accounts
      const state = location.state as any;
      if (state?.hasExistingAccounts) {
        return 'Sign agreements for new accounts';
      }
      return "Let's get to know each other";
    }
  };

  const getSubtitle = () => {
    switch (currentPhase) {
      case 1:
        return 'Personal information';
      case 2:
        return 'Employment information';
      case 3:
        return 'Tax information';
      case 4:
        return 'Income information';
      case 5:
        return 'Optional information';
      case 6: {
        // check if it is from existing accounts
        const state = location.state as { hasExistingAccounts?: boolean } | null;
        if (state?.hasExistingAccounts) {
          return 'Please review and sign the agreements for your new accounts';
        }
        return 'Wavvest Legal Agreement';
      }
      default:
        return 'Information';
    }
  };

  // add validate function
  const validateTaxId = (taxId: string): boolean => {
    return taxId.length === 9 && /^\d{9}$/.test(taxId);
  };

  const validateStartYear = (year: string): boolean => {
    const currentYear = new Date().getFullYear();
    const parsed = parseInt(year);
    return parsed >= 1991 && parsed <= currentYear;
  };

  // update Phase 2 form
  const renderPhase2Form = () => (
    <Form
      form={form}
      name="employmentInfo"
      onFinish={handleNext}
      layout="vertical"
      size="large"
      className="phase-form"
    >
      <div className="form-row">
        <Form.Item
          name="employmentStatus"
          label="Employment Status (single choice)"
          rules={[{ required: true, message: 'Please select your employment status' }]}
          className="form-item single"
        >
          <Radio.Group
            onChange={(e) => {
              setEmploymentStatus(e.target.value);
              updateFieldInStore('employmentStatus', e.target.value);
            }}
          >
            <Radio value="EMPLOYED">Employed</Radio>
            <Radio value="SELF_EMPLOYED">Self Employed</Radio>
            <Radio value="UNEMPLOYED">Unemployed</Radio>
            <Radio value="RETIRED">Retired</Radio>
            <Radio value="STUDENT">Student</Radio>
          </Radio.Group>
        </Form.Item>
      </div>

      <div className="form-row">
        <Form.Item
          name="occupation"
          label={`Job title${employmentStatus === 'EMPLOYED' || employmentStatus === 'SELF_EMPLOYED' ? '*' : ''}`}
          rules={[
            {
              validator: (_, value) => {
                // Required when employed or self-employed
                if (employmentStatus === 'EMPLOYED' || employmentStatus === 'SELF_EMPLOYED') {
                  if (!value || value.trim().length === 0) {
                    return Promise.reject(new Error('Occupation cannot be empty.'));
                  }
                }

                // Validate special characters if value present
                if (value) {
                  const err = validateOccupation(value);
                  if (err) {
                    return Promise.reject(new Error(err));
                  }
                }

                return Promise.resolve();
              },
            },
          ]}
          className="form-item single"
        >
          <Input
            placeholder="Enter your job title"
            onChange={(e) => {
              form.setFieldValue('occupation', e.target.value);
              updateFieldInStore('occupation', e.target.value);
            }}
          />
        </Form.Item>
      </div>

      <div className="form-row">
        <Form.Item
          name="employer"
          label="Employer name"
          rules={[
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();

                const err = validateEmployer(value);
                if (err) {
                  return Promise.reject(new Error(err));
                }

                return Promise.resolve();
              },
            },
          ]}
          className="form-item single"
        >
          <Input
            placeholder="Enter your employer name"
            onChange={(e) => {
              form.setFieldValue('employer', e.target.value);
              updateFieldInStore('employer', e.target.value);
            }}
          />
        </Form.Item>
      </div>

      <div className="form-row">
        <Form.Item
          name="startYear"
          label="Start year"
          rules={[
            {
              validator: (_, value) => {
                if (!value) return Promise.resolve();
                if (!validateStartYear(value)) {
                  const currentYear = new Date().getFullYear();
                  return Promise.reject(
                    new Error(`Start year must be in the range [1991, ${currentYear}]`),
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
          className="form-item single"
        >
          <Input
            placeholder="Enter start year"
            type="number"
            min="1991"
            max={new Date().getFullYear()}
            onChange={(e) => updateFieldInStore('startYear', e.target.value)}
          />
        </Form.Item>
      </div>
    </Form>
  );

  // update Phase 3 form
  const renderPhase3Form = () => (
    <Form
      form={form}
      name="taxInfo"
      onFinish={handleNext}
      layout="vertical"
      size="large"
      className="phase-form"
    >
      <div className="form-row">
        <Form.Item
          name="taxIdType"
          label="Type of ID"
          rules={[{ required: true, message: 'Please select your tax ID type' }]}
          className="form-item single"
          initialValue="TAX_ID_TYPE_SSN"
        >
          <Select
            placeholder="Select tax ID type"
            onChange={(value) => updateFieldInStore('taxIdType', value)}
          >
            <Option value="TAX_ID_TYPE_SSN">SSN</Option>
            <Option value="TAX_ID_TYPE_ITIN">ITIN</Option>
            <Option value="TAX_ID_TYPE_EIN">EIN</Option>
          </Select>
        </Form.Item>
      </div>

      <div className="form-row">
        <Form.Item
          name="taxId"
          label="Tax ID number"
          rules={[
            { required: true, message: 'Please enter your tax ID number' },
            {
              validator: (_, value) => {
                if (!value || validateTaxId(value)) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Tax ID must be exactly 9 digits'));
              },
            },
          ]}
          className="form-item single"
        >
          <Input
            placeholder="Enter your 9-digit tax ID number"
            maxLength={9}
            onChange={(e) => {
              const sanitized = e.target.value.replace(/[^0-9]/g, '');
              form.setFieldValue('taxId', sanitized);
              updateFieldInStore('taxId', sanitized);
            }}
          />
        </Form.Item>
      </div>
    </Form>
  );

  // update Phase 4 form - use range selection instead of specific values
  const renderPhase4Form = () => {
    const usdRangeOptions = [
      { value: 'USD_RANGE_UNSPECIFIED', label: 'Do not know' },
      { value: 'UNDER_25K', label: 'Less than 25k' },
      { value: 'FROM_25K_TO_50K', label: 'Between 25k to 50k' },
      { value: 'FROM_50K_TO_100K', label: 'Between 50k to 100k' },
      { value: 'FROM_100K_TO_200K', label: 'Between 100k to 200k' },
      { value: 'FROM_200K_TO_300K', label: 'Between 200k to 300k' },
      { value: 'FROM_300K_TO_500K', label: 'Between 300k to 500k' },
      { value: 'FROM_500K_TO_1M', label: 'Between 500k to 1 million' },
      { value: 'FROM_1M_TO_5M', label: 'Between 1 million to 5 million' },
      { value: 'OVER_5M', label: 'Over 5 million' },
    ];

    return (
      <Form
        form={form}
        name="incomeInfo"
        onFinish={handleNext}
        layout="vertical"
        size="large"
        className="phase-form"
      >
        <div className="form-row">
          <Form.Item
            name="annualIncomeRangeUsd"
            label="Annual income in USD"
            rules={[
              {
                required: true,
                message: 'Please select your annual income range',
              },
            ]}
            className="form-item single"
          >
            <Select
              placeholder="Select annual income range"
              onChange={(value) => updateFieldInStore('annualIncomeRangeUsd', value)}
            >
              {usdRangeOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>

        <div className="form-row">
          <Form.Item
            name="totalNetWorthRangeUsd"
            label="Total networth in USD"
            rules={[
              {
                required: true,
                message: 'Please select your total net worth range',
              },
            ]}
            className="form-item single"
          >
            <Select
              placeholder="Select total net worth range"
              onChange={(value) => updateFieldInStore('totalNetWorthRangeUsd', value)}
            >
              {usdRangeOptions.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </Form>
    );
  };

  // update Phase 5 form - add all necessary optional fields
  const renderPhase5Form = () => (
    <Form
      form={form}
      name="optionalInfo"
      onFinish={handleNext}
      layout="vertical"
      size="large"
      className="phase-form"
    >
      <div className="form-row">
        <Form.Item
          name="investmentExperience"
          label="Investment experience"
          className="form-item single"
        >
          <Select
            placeholder="Select investment experience"
            onChange={(value) => updateFieldInStore('investmentExperience', value)}
          >
            <Option value="INVESTMENT_EXPERIENCE_UNSPECIFIED">Do not wish to provide</Option>
            <Option value="NONE">No experience</Option>
            <Option value="LIMITED">Limited</Option>
            <Option value="GOOD">Good</Option>
            <Option value="EXTENSIVE">Extensive</Option>
          </Select>
        </Form.Item>
      </div>

      <div className="form-row">
        <Form.Item name="maritalStatus" label="Marital status" className="form-item single">
          <Select
            placeholder="Select marital status"
            onChange={(value) => updateFieldInStore('maritalStatus', value)}
          >
            <Option value="MARITAL_STATUS_UNSPECIFIED">Do not wish to provide</Option>
            <Option value="SINGLE">Single</Option>
            <Option value="MARRIED">Married</Option>
            <Option value="DIVORCED">Divorced</Option>
            <Option value="WIDOWED">Widowed</Option>
          </Select>
        </Form.Item>
      </div>

      {/* large trader status */}
      <div className="form-row">
        <Form.Item
          name="isLargeTrader"
          label={
            <LabelWithTooltip
              label="Are you a large trader?"
              tooltip="SEC defines 'Large Trader' as a person whose transactions in NMS securities equal or exceed 2 million shares or $20 million during any calendar day, or 20 million shares or $200 million during any calendar month"
            />
          }
          className="form-item single"
        >
          <Radio.Group
            onChange={(e) => {
              setIsLargeTrader(e.target.value);
              updateFieldInStore('isLargeTrader', e.target.value);
            }}
          >
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </Form.Item>
      </div>

      {isLargeTrader && (
        <>
          <div className="form-row">
            <Form.Item
              name={['largeTrader', 'id']}
              label="Large trader Id"
              rules={[
                { required: true, message: 'Please enter large trader ID' },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();

                    // Check if it's exactly 12 digits (numeric only)
                    if (!/^\d{12}$/.test(value)) {
                      return Promise.reject(new Error('Large trader ID must be exactly 12 digits'));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
              className="form-item single"
            >
              <Input
                placeholder="Enter 12-digit large trader ID"
                maxLength={12}
                onChange={(e) => {
                  // Only allow digits
                  const sanitized = e.target.value.replace(/[^0-9]/g, '');
                  form.setFieldValue(['largeTrader', 'id'], sanitized);
                  updateNestedFieldInStore('largeTrader', 'id', sanitized);
                }}
              />
            </Form.Item>
          </div>

          <div className="form-row">
            <Form.Item
              name={['largeTrader', 'effectiveDate']}
              label="Effective date"
              rules={[{ required: true, message: 'Please select effective date' }]}
              className="form-item single"
            >
              <DatePicker
                placeholder="Select effective date"
                style={{ width: '100%' }}
                disabledDate={(current) => {
                  // Disable dates after today
                  return current && current > dayjs().endOf('day');
                }}
                onChange={(date) => {
                  if (date) {
                    // Store dayjs object in form (for DatePicker)
                    form.setFieldValue(['largeTrader', 'effectiveDate'], date);
                    const effectiveDate = {
                      day: date.date(),
                      month: date.month() + 1,
                      year: date.year(),
                    };
                    updateNestedFieldInStore('largeTrader', 'effectiveDate', effectiveDate);
                  }
                }}
              />
            </Form.Item>
          </div>
        </>
      )}

      {/* financial advisor status */}
      <div className="form-row">
        <Form.Item
          name="isAdvisor"
          label="Are you a financial advisor?"
          className="form-item single"
        >
          <Radio.Group onChange={(e) => updateFieldInStore('isAdvisor', e.target.value)}>
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </Form.Item>
      </div>

      {/* institutional customer status */}
      <div className="form-row">
        <Form.Item
          name="isInstitutionalCustomer"
          label="Institutional customer?"
          className="form-item single"
        >
          <Radio.Group
            onChange={(e) => updateFieldInStore('isInstitutionalCustomer', e.target.value)}
          >
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </Form.Item>
      </div>

      {/* Apex employee status */}
      <div className="form-row">
        <Form.Item
          name="isCorrespondentEmployee"
          label="Are you an employee of Apex Fintech Solutions?"
          className="form-item single"
        >
          <Radio.Group
            onChange={(e) => updateFieldInStore('isCorrespondentEmployee', e.target.value)}
          >
            <Radio value={true}>Yes</Radio>
            <Radio value={false}>No</Radio>
          </Radio.Group>
        </Form.Item>
      </div>

      {/* FINRA association */}
      <div className="form-row">
        <Form.Item
          name="isFinraAffiliated"
          className="form-item single"
          style={{ textAlign: 'left' }}
          valuePropName="checked"
        >
          <Checkbox
            checked={showFinraEntity}
            onChange={(e) => {
              setShowFinraEntity(e.target.checked);
              updateFieldInStore('isFinraAffiliated', e.target.checked);
              if (!e.target.checked) {
                updateFieldInStore('finraAssociatedEntity', undefined);
              }
            }}
          >
            Are you affiliated with a FINRA entity?
          </Checkbox>
        </Form.Item>
      </div>

      {showFinraEntity && (
        <div className="form-row">
          <Form.Item
            name="finraAssociatedEntity"
            label="The name of the FINRA-associated entity that you are affiliated with"
            className="form-item single"
            style={{ textAlign: 'left' }}
          >
            <Input
              placeholder="Enter FINRA entity name"
              onChange={(e) => updateFieldInStore('finraAssociatedEntity', e.target.value)}
            />
          </Form.Item>
        </div>
      )}

      {/* control person status */}
      <div className="form-row">
        <Form.Item
          name="isControlPerson"
          className="form-item single"
          style={{ textAlign: 'left' }}
          valuePropName="checked"
        >
          <Checkbox
            checked={showControlPerson}
            onChange={(e) => {
              setShowControlPerson(e.target.checked);
              updateFieldInStore('isControlPerson', e.target.checked);
              if (!e.target.checked) {
                updateFieldInStore('controlPersonCompanySymbols', undefined);
              }
            }}
          >
            <LabelWithTooltip
              label="Are you a control person of companies?"
              tooltip="Control persons are defined as having significant influence over a company's management and operations, typically through ownership of a large percentage of the company's voting stock or through positions on the company's board of directors or executive team."
            />
          </Checkbox>
        </Form.Item>
      </div>

      {showControlPerson && (
        <div className="form-row">
          <Form.Item
            name="controlPersonCompanySymbols"
            label="List the ticker symbols of those companies, separated by commas."
            className="form-item single"
            style={{ textAlign: 'left' }}
          >
            <Input
              placeholder="Enter company symbols (comma separated)"
              onChange={(e) => {
                const symbols = e.target.value
                  .split(',')
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0);
                updateFieldInStore('controlPersonCompanySymbols', symbols);
              }}
            />
          </Form.Item>
        </div>
      )}

      {/* politically exposed family members */}
      <div className="form-row">
        <Form.Item
          name="hasPoliticallyExposedFamily"
          className="form-item single"
          style={{ textAlign: 'left' }}
          valuePropName="checked"
        >
          <Checkbox
            checked={showPoliticallyExposedFamily}
            onChange={(e) => {
              setShowPoliticallyExposedFamily(e.target.checked);
              updateFieldInStore('hasPoliticallyExposedFamily', e.target.checked);
              if (!e.target.checked) {
                updateFieldInStore('politicallyExposedFamilyNames', undefined);
              }
            }}
          >
            Are any of your family members politically exposed?
          </Checkbox>
        </Form.Item>
      </div>

      {showPoliticallyExposedFamily && (
        <div className="form-row">
          <Form.Item
            name="politicallyExposedFamilyNames"
            label="A list of names representing the politically exposed family members"
            className="form-item single"
            style={{ textAlign: 'left' }}
          >
            <Input.TextArea
              placeholder="Enter family member names and their positions"
              rows={3}
              onChange={(e) => updateFieldInStore('politicallyExposedFamilyNames', e.target.value)}
            />
          </Form.Item>
        </div>
      )}

      {/* politically exposed organization */}
      <div className="form-row">
        <Form.Item
          name="hasPoliticallyExposedOrg"
          className="form-item single"
          style={{ textAlign: 'left' }}
          valuePropName="checked"
        >
          <Checkbox
            checked={showPoliticallyExposedOrg}
            onChange={(e) => {
              setShowPoliticallyExposedOrg(e.target.checked);
              updateFieldInStore('hasPoliticallyExposedOrganization', e.target.checked);
              if (!e.target.checked) {
                updateFieldInStore('politicallyExposedOrganization', undefined);
              }
            }}
          >
            Are you associated with any politically exposed organization?
          </Checkbox>
        </Form.Item>
      </div>

      {showPoliticallyExposedOrg && (
        <div className="form-row">
          <Form.Item
            name="politicallyExposedOrganization"
            label="A list of names of politically exposed organizations that you are personally associated with."
            className="form-item single"
            style={{ textAlign: 'left' }}
          >
            <Input.TextArea
              placeholder="Enter organization name and describe your role"
              rows={3}
              onChange={(e) => updateFieldInStore('politicallyExposedOrganization', e.target.value)}
            />
          </Form.Item>
        </div>
      )}

      {/* trusted contact section - only allow adding one */}
      <div className="form-row">
        <Button
          type="primary"
          onClick={() => setShowTrustedContact(!showTrustedContact)}
          className="next-button"
          size="large"
        >
          {showTrustedContact ? 'Hide' : 'Add'} Trusted Contact
        </Button>
      </div>

      {showTrustedContact && (
        <>
          <div className="trusted-contact-info">
            <p>
              A Trusted Contact is a person designated to verify the well being of the account
              holder.
            </p>
          </div>
          <div className="form-row">
            <Form.Item
              name="trustedContactGivenName"
              label="First legal name"
              rules={[
                {
                  required: true,
                  message: 'First name is required',
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const error = validateName(value);
                    if (error) {
                      return Promise.reject(new Error(error));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              className="form-item"
            >
              <Input
                placeholder="Enter First legal name"
                onChange={(e) =>
                  updateTrustedContactInStore('trustedContactGivenName', e.target.value)
                }
              />
            </Form.Item>
            <Form.Item
              name="trustedContactMiddleNames"
              label="Middle names"
              className="form-item single"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const middleNamesArray = parseMiddleNames(value);
                    const error = validateMiddleNames(middleNamesArray);
                    if (error) {
                      return Promise.reject(new Error(error));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              extra="Separate multiple names with spaces (e.g., 'John Michael')"
            >
              <Input
                placeholder="Enter middle names (separated by spaces)"
                onChange={(e) =>
                  updateTrustedContactInStore('trustedContactMiddleNames', e.target.value)
                }
              />
            </Form.Item>
            <Form.Item
              name="trustedContactFamilyName"
              label="Last legal name"
              rules={[
                {
                  required: true,
                  message: 'Last name is required',
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const error = validateName(value);
                    if (error) {
                      return Promise.reject(new Error(error));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              className="form-item"
            >
              <Input
                placeholder="Enter Last legal name"
                onChange={(e) =>
                  updateTrustedContactInStore('trustedContactFamilyName', e.target.value)
                }
              />
            </Form.Item>
          </div>
          <div className="form-row">
            <Form.Item
              name="trustedContactPhone"
              label="Phone number"
              rules={[
                {
                  required: true,
                  message: 'Phone number is required',
                },
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const error = validateE164PhoneNumber(value);
                    if (error) {
                      return Promise.reject(new Error(error));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              className="form-item single"
              extra="Please use E.164 format (e.g., +12125551234)"
            >
              <PhoneNumberInput
                value={userInfo.trustedContacts?.[0]?.phoneNumber || ''}
                placeholder="Enter phone number (E.164 format, e.g., +1...)"
                onChange={
                  (value) => updateTrustedContactInStore('trustedContactPhone', value)
                  // <Input
                  //   placeholder="Enter phone number (E.164 format, e.g., +12125551234)"
                  //   onChange={(e) =>
                  //     updateTrustedContactInStore(
                  //       "trustedContactPhone",
                  //       e.target.value,
                  //     )
                }
              />
            </Form.Item>
          </div>
          <div className="form-row">
            <Form.Item
              name="trustedContactEmail"
              label="Email"
              rules={[
                {
                  required: true,
                  message: 'Email is required',
                },
                {
                  type: 'email',
                  message: 'Please enter a valid email address',
                },
              ]}
              className="form-item single"
            >
              <Input
                placeholder="Enter email address"
                type="email"
                onChange={(e) => updateTrustedContactInStore('trustedContactEmail', e.target.value)}
              />
            </Form.Item>
          </div>
          {/* Mailing address - structured format */}
          <div className="form-row">
            <Form.Item
              name="trustedContactAddressLine1"
              label="Street Address 1"
              className="form-item single"
              rules={[
                {
                  required: true,
                  message: 'Please enter street address',
                },
              ]}
            >
              <Input
                placeholder="Enter street address"
                onChange={(e) =>
                  updateTrustedContactInStore('trustedContactAddressLine1', e.target.value)
                }
              />
            </Form.Item>
          </div>
          <div className="form-row">
            <Form.Item
              name="trustedContactAddressLine2"
              label="Street Address 2"
              className="form-item single"
            >
              <Input
                onChange={(e) =>
                  updateTrustedContactInStore('trustedContactAddressLine2', e.target.value)
                }
              />
            </Form.Item>
          </div>
          <div className="form-row">
            <Form.Item
              name="trustedContactAddressCity"
              label="City"
              className="form-item single"
              rules={[{ required: true, message: 'Please enter city' }]}
            >
              <Input
                placeholder="Enter city"
                onChange={(e) =>
                  updateTrustedContactInStore('trustedContactAddressCity', e.target.value)
                }
              />
            </Form.Item>
          </div>
          {trustedContactCountry === 'US' && (
            <div className="form-row">
              <Form.Item
                name="trustedContactAddressState"
                label="State"
                className="form-item single"
                rules={[{ required: true, message: 'Please select state' }]}
              >
                <Select
                  placeholder="Select state"
                  showSearch
                  onChange={(value) =>
                    updateTrustedContactInStore('trustedContactAddressState', value)
                  }
                  options={US_STATES.map((state) => ({
                    value: state,
                    label: state,
                  }))}
                />
              </Form.Item>
            </div>
          )}
          {trustedContactCountry !== 'US' && (
            <div className="form-row">
              <Form.Item
                name="trustedContactAddressState"
                label="State/Province/Region"
                className="form-item single"
                rules={[
                  {
                    required: true,
                    message: 'Please enter state/province/region',
                  },
                ]}
              >
                <Input
                  placeholder="Enter state/province/region"
                  onChange={(e) =>
                    updateTrustedContactInStore('trustedContactAddressState', e.target.value)
                  }
                />
              </Form.Item>
            </div>
          )}
          <div className="form-row">
            <Form.Item
              name="trustedContactAddressPostalCode"
              label="Postal code"
              className="form-item single"
              rules={[
                { required: true, message: 'Please enter postal code' },
                ...(trustedContactCountry === 'US'
                  ? [
                      {
                        pattern: /^[0-9]{5}(-[0-9]{4})?$/,
                        message: 'Please enter a valid US postal code (e.g., 12345 or 12345-6789)',
                      },
                    ]
                  : [
                      {
                        pattern: /^[A-Za-z0-9\s-]{3,10}$/,
                        message: 'Please enter a valid postal code',
                      },
                    ]),
              ]}
            >
              <Input
                placeholder={
                  trustedContactCountry === 'US'
                    ? 'Enter postal code (e.g., 12345)'
                    : 'Enter postal code'
                }
                onChange={(e) =>
                  updateTrustedContactInStore('trustedContactAddressPostalCode', e.target.value)
                }
              />
            </Form.Item>
          </div>
          <div className="form-row">
            <Form.Item
              name="trustedContactAddressCountry"
              label="Country"
              className="form-item single"
              rules={[{ required: true, message: 'Please select country' }]}
              initialValue="US"
            >
              <Select
                placeholder="Select country"
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onChange={(value) => {
                  setTrustedContactCountry(value as CountryType);
                  updateTrustedContactInStore('trustedContactAddressCountry', value);
                  // Clear state field when switching countries
                  if (value !== 'US') {
                    form.setFieldValue('trustedContactAddressState', '');
                    updateTrustedContactInStore('trustedContactAddressState', '');
                  }
                }}
                options={ISO_3166_1_ALPHA_2_CODES.map((code) => ({
                  value: code,
                  label: getCountryName(code),
                }))}
              />
            </Form.Item>
          </div>
        </>
      )}

      {/* Letter 3210 reminder */}
      {(showFinraEntity || form.getFieldValue('isCorrespondentEmployee')) && (
        <div className="letter-3210-notice">
          <p>
            ℹ️ Letter 3210 is required for your account since you are affiliated with FINRA entity
            or Apex Fintech Solutions. Please provide your advisor with the required documents.
          </p>
        </div>
      )}
    </Form>
  );

  // load user basic info and agreements data
  useEffect(() => {
    const loadUserBasicInfo = async () => {
      const state = location.state as any;
      if (state?.hasExistingAccounts) {
        return;
      }

      if (firmDataLoading) {
        return;
      }

      let availableKycResult = kycResult;
      let nextUserInfo = latestUserInfoRef.current;

      if (firmDataEnabled) {
        try {
          const profile = await fetchApexOnboardingPrefill();
          const firmDataPrefill = getMissingApexPersonalInfoFromFirmData(
            latestUserInfoRef.current,
            profile as ApexOnboardingPrefill | null,
          );
          if (Object.keys(firmDataPrefill).length > 0) {
            nextUserInfo = {
              ...latestUserInfoRef.current,
              ...firmDataPrefill,
            };
            latestUserInfoRef.current = nextUserInfo;
            dispatch(updateUserInfo(firmDataPrefill));
          }
        } catch (error) {
          // KYC remains the fallback for missing firm-data profile values.
          console.warn('Unable to load firm-data profile for Apex prefill:', error);
        }
      }

      if (
        !availableKycResult.dateOfBirth &&
        !latestUserInfoRef.current.dateOfBirth &&
        !attemptedKycResultPrefillRef.current
      ) {
        attemptedKycResultPrefillRef.current = true;
        try {
          const verificationResult = await getVerificationResult();
          const fetchedKycResult = mapVerificationResultToKycResult(verificationResult);
          if (Object.keys(fetchedKycResult).length > 0) {
            availableKycResult = {
              ...availableKycResult,
              ...fetchedKycResult,
            };
            dispatch(setKycResult(availableKycResult));
          }
        } catch (error) {
          console.warn('Unable to load KYC result for account prefill:', error);
        }
      }

      // Prioritize KYC result from store for name, date of birth, and address.
      // Only auto-fill if the fields are empty in the store (first time loading).
      const prefill = getMissingApexPersonalInfoFromKyc(
        latestUserInfoRef.current,
        availableKycResult,
      );

      nextUserInfo = {
        ...latestUserInfoRef.current,
        ...prefill,
      };
      latestUserInfoRef.current = nextUserInfo;

      if (prefill.firstName && prefill.lastName) {
        updateFieldInStore('firstName', prefill.firstName);
        updateFieldInStore('lastName', prefill.lastName);
        setTimeout(() => {
          form.setFieldValue('firstName', prefill.firstName);
          form.setFieldValue('lastName', prefill.lastName);
        }, 150);
      }

      if (prefill.dateOfBirth) {
        updateFieldInStore('dateOfBirth', prefill.dateOfBirth);
        setTimeout(() => {
          form.setFieldValue(
            'dateOfBirth',
            dayjs(new Date(prefill.dateOfBirth!).toISOString().split('T')[0]),
          );
        }, 150);
      }

      if (prefill.personalAddress) {
        updateFieldInStore('personalAddress', prefill.personalAddress);
      }

      if (prefill.phoneNumber) {
        updateFieldInStore('phoneNumber', prefill.phoneNumber);
        setTimeout(() => {
          form.setFieldValue('phoneNumber', prefill.phoneNumber);
        }, 150);
      }

      if (hasCompleteApexPersonalInfoForKycSkip(nextUserInfo)) {
        setIsResolvingPersonalInfo(false);
        setCurrentPhase(2);
        return;
      }

      message.error('Please complete identity verification before opening a brokerage account.');
      navigate('/kyc');
    };
    if (currentPhase === 1) {
      loadUserBasicInfo();
    } else if (currentPhase === 6) {
      setIsResolvingPersonalInfo(false);
      getRiaProfile()
        .then((data) => {
          setAgreements(data.enabled_agreements);
        })
        .catch((error) => {
          console.error('Error loading agreements:', error);
        });
    } else {
      setIsResolvingPersonalInfo(false);
    }
  }, [currentPhase, firmDataEnabled, firmDataLoading]);

  useEffect(() => {
    if (currentPhase === 6) {
      // show loading only when accounts is empty
      // if accounts already has data, don't show loading
      if (apexAccounts.length === 0) {
        setAgreementsLoading(true);
      }

      try {
        // apexAccounts.forEach((account) => {
        //   if (!account.account_detail) {
        //     dispatch(fetchAccountDetails(account.id));
        //   }
        // });

        if (!apexLoading.details) {
          const agreementsToSign = apexAccounts
            .map((account: ApexAccount) => {
              const agreements = (account.account_detail?.agreements ?? []).filter(
                (a) => a.agreement_state != 'AFFIRMED',
              );
              return {
                account,
                agreements,
              };
            })
            .filter(
              ({ agreements }: { agreements: components['schemas']['Agreement'][] }) =>
                agreements.length > 0,
            );
          setApexAgreements(agreementsToSign);
        }
      } catch (error) {
        console.error('Error fetching account details:', error);
      }

      // if apexAccounts already has data, close loading state
      if (apexAccounts.length > 0) {
        setAgreementsLoading(false);
      }
    }
  }, [currentPhase, apexAccounts]);

  // The open-account wizard still needs Apex goal-typed accounts even when
  // Wealth skips the legacy fetch for firm-data firms. If this firm has Apex
  // and the list is empty, fetch on mount (and again at phase 6 for agreements).
  useEffect(() => {
    const apexEnabled =
      Boolean(riaProfile.profile?.apex_correspondent_id) &&
      Boolean(riaProfile.features?.apex_enabled);
    if (!apexEnabled || apexAccounts.length > 0) {
      return;
    }

    if (currentPhase === 6) {
      setAgreementsLoading(true);
    }

    try {
      dispatch(fetchApexAccounts());
    } catch (error) {
      console.error('Error fetching apexAccounts:', error);
      if (currentPhase === 6) {
        setAgreementsLoading(false);
      }
    }
  }, [
    currentPhase,
    apexAccounts.length,
    dispatch,
    riaProfile.profile?.apex_correspondent_id,
    riaProfile.features?.apex_enabled,
  ]);

  // update the agreement page to show the real agreements
  const renderPhase6Form = () => {
    return (
      <Form
        form={form}
        name="legalAgreement"
        onFinish={handleNext}
        layout="vertical"
        size="large"
        className="phase-form"
      >
        <div className="legal-agreement-container">
          <div className="agreement-subtitle">
            {/* <p>Please review the following documents before proceeding:</p> */}
            <p>Opening Account Legal Agreements</p>
          </div>
          <div className="agreements-list">
            {agreements.map((agreement: ClientAgreementsToSignRecord, index) => (
              <div key={agreement.agreement_id || index} className="agreement-item">
                <Button
                  onClick={() => window.open(agreement.url, '_blank')}
                  className="agreement-button"
                  size="large"
                  color="blue"
                >
                  <Tooltip title={agreement.description}>{agreement.title}</Tooltip>
                </Button>
              </div>
            ))}
          </div>
          <div className="agreements-list">
            {agreementsLoading ? (
              <Spin />
            ) : (
              apexAgreements.map((item: ApexAgreementsToSignRecord, index) => (
                <div key={item.account.id || index} className="agreement-item">
                  <p className="title">{accountTypeToName(item.account.type)}</p>
                  {item.agreements.map((agreement) => (
                    <Button
                      key={agreement.agreement_id}
                      onClick={() => window.open(agreement.agreement_uri!, '_blank')}
                      className="agreement-button"
                      size="large"
                      color="blue"
                    >
                      {agreement.agreement_name}
                    </Button>
                  ))}
                </div>
              ))
            )}
          </div>

          <div className="agreement-checkbox">
            <Form.Item
              name="agreementAccepted"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) => {
                    if (value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('You must agree to the terms to proceed'));
                  },
                },
              ]}
            >
              <Checkbox
                onChange={(e) => {
                  setAllFormData((prev) => ({
                    ...prev,
                    agreementAccepted: e.target.checked,
                  }));
                }}
              >
                I agree to the terms of all the documents listed above
              </Checkbox>
            </Form.Item>
          </div>
        </div>
      </Form>
    );
  };

  const renderCurrentForm = () => {
    if (currentPhase === 1 && isResolvingPersonalInfo) {
      return (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
          <Spin size="large" />
        </div>
      );
    }

    switch (currentPhase) {
      case 1:
        return renderPhase2Form();
      case 2:
        return renderPhase2Form();
      case 3:
        return renderPhase3Form();
      case 4:
        return renderPhase4Form();
      case 5:
        return renderPhase5Form();
      case 6:
        return renderPhase6Form();
      default:
        return renderPhase2Form();
    }
  };

  return (
    <div className="step2-container">
      <div className="header">
        <span className="back-button" onClick={handleBack}>
          <LeftOutlined />
        </span>
        <h1 className="title">{getTitle()}</h1>
      </div>

      <div className="content">
        <div className="progress-container">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${(currentPhase / totalPhases) * 100}%` }}
            />
          </div>
          <div className="progress-text">
            {currentPhase}/{totalPhases}
          </div>
        </div>

        <div className="subtitle-container">
          <h2 className="subtitle">{getSubtitle()}</h2>
        </div>

        <div className="form-container">{renderCurrentForm()}</div>

        <div className="footer">
          {currentPhase === 6 && (
            <Button
              size="large"
              onClick={handleSkip}
              loading={loading}
              className="skip-phase-button"
            >
              Skip
            </Button>
          )}
          <Button
            type="primary"
            size="large"
            onClick={() => form.submit()}
            loading={loading}
            className="next-button"
          >
            {currentPhase === 6 ? 'I Agree' : 'Next'}
          </Button>
        </div>
      </div>

      {/* submit confirm modal */}
      <Modal
        title="Confirm"
        open={isSubmitConfirmModalVisible}
        onOk={handleSubmitConfirm}
        onCancel={handleSubmitCancel}
        footer={[
          <Button key="cancel" className="skip-phase-button" onClick={handleSubmitCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            className="next-button"
            onClick={handleSubmitConfirm}
            loading={loading}
          >
            Submit
          </Button>,
        ]}
        className="confirm-modal"
      >
        <div className="modal-content">
          <p>
            By clicking the "Submit" button, you will be able to open accounts through Apex Fintech
            Solutions. See{' '}
            <a
              href="https://wavvest-public-documents.s3.us-west-2.amazonaws.com/Apex/Apex-Form-CRS.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '12px', textDecoration: 'underline' }}
            >
              Apex's CRS Form
            </a>{' '}
            and{' '}
            <a
              href="https://reports.adviserinfo.sec.gov/crs/crs_332096.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: '12px', textDecoration: 'underline' }}
            >
              Wavvest's CRS Form.
            </a>
          </p>
        </div>
      </Modal>

      {/* Success Modal */}
      <Modal open={isSuccessModalVisible} footer={null} centered width={400} closable={false}>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <CheckCircleOutlined
            style={{
              fontSize: '64px',
              color: '#52c41a',
              marginBottom: '24px',
            }}
          />
          <p
            style={{
              fontSize: '16px',
              marginBottom: '32px',
            }}
          >
            You're all set! Let's make the most of your new account.
          </p>
          <Button
            type="primary"
            size="large"
            onClick={handleBackToHomepage}
            className="next-button"
          >
            Back to homepage
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default Step2;
